import { ChipEstado } from '@/components/ui/ChipEstado'
import { IconoTipo } from '@/features/publicacion/IconoTipo'
import { MediaSlot } from '@/features/publicacion/MediaSlot'
import { cn } from '@/lib/cn'
import { useEsCelular } from '@/lib/useEsEscritorio'
import type { PublicacionResumen } from '@/lib/schemas/publicaciones'
import { formatearDiaTarjeta } from './formato'

interface TarjetaPublicacionProps {
  publicacion: PublicacionResumen
  esRastro: boolean
  alAbrir: (id: string) => void
  /**
   * Tarjeta angosta de escritorio (monitor de 1360×768, ~170 px): el chip va
   * en su forma corta, como en el celular, para no pisarse con el punto.
   */
  angosta?: boolean
}

/**
 * C2 · tarjeta de la grilla (§8.2). Portada 4:5 con el espacio reservado
 * desde el inicio, chip de estado arriba a la izquierda, punto de novedad
 * arriba a la derecha e ícono de tipo cuando corresponde. La vista de RASTRO
 * suma la fecha con la versión, superpuesta sobre la portada para no restarle
 * lugar a la grilla.
 *
 * Una historia (DEC-21) lleva el anillo de color de Instagram y la etiqueta
 * "Historia" bajo el chip: se ve en la grilla junto al feed (OBS-017). Al
 * pasar el mouse la tarjeta se levanta (DEC-23).
 *
 * La tarjeta no tiene menú (revisión OO del 21/09/2026): dos caminos para
 * editar confundían. Editar, Reprogramar, Dar de baja y Restaurar están en el
 * panel de la publicación, que se abre al tocarla.
 */
export function TarjetaPublicacion({
  publicacion: p,
  esRastro,
  alAbrir,
  angosta = false,
}: TarjetaPublicacionProps) {
  const esCelular = useEsCelular()
  const corta = esCelular || angosta
  const esHistoria = p.destino === 'historia'
  return (
    <article
      data-publicacion={p.titulo}
      data-destino={p.destino}
      className={cn(p.dadaDeBaja && 'opacity-60')}
    >
      <div className="al-pasar relative rounded-lg sm:rounded-tarjeta">
        <button
          type="button"
          onClick={() => {
            alAbrir(p.id)
          }}
          // Radio más chico en celular: con tres columnas angostas, el del
          // sistema se comía la foto (pedido de Serena, 22/09).
          className="block w-full overflow-hidden rounded-lg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-tinta sm:rounded-tarjeta"
          // La portada es decorativa: el nombre de la pieza lo da este botón.
          aria-label={`Abrir ${p.titulo}`}
        >
          <MediaSlot
            aspect="4:5"
            driveFileId={p.portada?.driveFileId}
            blurhash={p.portada?.blurhash ?? undefined}
            miniaturaGuardada={p.portada?.miniatura}
            className="w-full"
          />
        </button>

        {esHistoria && (
          <span
            aria-hidden
            className="pointer-events-none absolute inset-0 rounded-lg ring-2 ring-historia ring-inset sm:rounded-tarjeta sm:ring-[3px]"
          />
        )}

        <span className="pointer-events-none absolute top-2 left-2 flex flex-col items-start gap-1 sm:top-3 sm:left-3">
          {/* En celular la tarjeta es angosta: el chip va en su forma corta. */}
          <ChipEstado
            estado={p.estado}
            forma={corta ? 'corta' : 'larga'}
            paraRastro={esRastro}
            // En celular la tarjeta es angosta: el chip se achica para no
            // pisarse con el ícono de carrusel o video.
            className={corta ? 'px-2 py-0.5 text-[11px] leading-4' : undefined}
          />
          {esHistoria && (
            <span
              data-marca-historia
              className={cn(
                'rounded-full bg-historia font-medium text-white',
                corta ? 'px-2 py-0.5 text-[11px] leading-4' : 'px-2.5 py-0.5 text-xs',
              )}
            >
              Historia
            </span>
          )}
        </span>

        {p.nuevo && (
          <span
            data-nuevo
            className="pointer-events-none absolute top-3 right-3 size-3 rounded-full bg-nuevo ring-2 ring-white sm:top-4 sm:right-4"
            aria-label="Novedad desde tu última visita"
            role="img"
          />
        )}

        {esRastro && (
          <p
            data-fecha-tarjeta
            className="pointer-events-none absolute inset-x-0 bottom-0 truncate rounded-b-tarjeta bg-gradient-to-t from-tinta/75 to-transparent px-2 pt-6 pb-1.5 text-xs font-medium text-white sm:px-3 sm:pb-2 sm:text-sm"
          >
            {formatearDiaTarjeta(p.fechaPautada, p.horaPautada)} · v{p.versionActual}
            {p.dadaDeBaja && ' · dada de baja'}
          </p>
        )}

        {p.tipo !== 'imagen' && !p.nuevo && (
          <IconoTipo
            tipo={p.tipo}
            className="absolute top-2 right-2 flex size-7 items-center justify-center rounded-lg bg-white/90 text-tinta sm:top-3 sm:right-3 sm:size-8"
            claseIcono="size-4"
            alHacerClic={() => {
              alAbrir(p.id)
            }}
          />
        )}
      </div>
    </article>
  )
}
